<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import MentionsAPI from 'dashboard/api/mentions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import { humanizeSince } from '../helpers';

defineProps({
  mentions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['dismissed']);

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const openConversation = mention => {
  router.push(
    frontendURL(
      `accounts/${accountId.value}/conversations/${mention.conversation_id}`
    )
  );
};

const dismissingId = ref(null);

const dismissMention = async mention => {
  dismissingId.value = mention.id;
  try {
    // Igual que clearPriority en PriorityCasesView: se llama a la API directo
    // para enterarnos si el descarte falla de verdad, en vez de sacar la
    // mencion de la vista sin haberla descartado en el backend.
    await MentionsAPI.dismiss(mention.id);
    emit('dismissed', mention.id);
  } catch (error) {
    useAlert(t('SICARG_DASHBOARD.MENTIONS.DISMISS_ERROR'));
  } finally {
    dismissingId.value = null;
  }
};
</script>

<template>
  <CardLayout>
    <h2 class="text-sm font-medium text-n-slate-12">
      {{ t('SICARG_DASHBOARD.MENTIONS.TITLE') }}
    </h2>
    <div
      class="overflow-y-auto rounded-lg border divide-y border-n-weak divide-n-weak max-h-[420px]"
    >
      <div
        v-for="mention in mentions"
        :key="mention.id"
        class="flex justify-between items-center px-4 py-3 w-full hover:bg-n-alpha-1"
      >
        <button
          class="flex flex-col flex-grow text-left"
          @click="openConversation(mention)"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ mention.contact?.name || t('SICARG_DASHBOARD.LIST.NO_NAME') }}
            <span class="ml-1 text-xs text-n-slate-10"
              >#{{ mention.conversation_id }}</span
            >
          </span>
          <span v-if="mention.numero_cuenta" class="text-xs text-n-slate-11">
            {{ t('SICARG_DASHBOARD.LIST.ACCOUNT') }}: {{ mention.numero_cuenta }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('SICARG_DASHBOARD.MENTIONS.MENTIONED_AT') }}:
            {{ humanizeSince(mention.mentioned_at) }}
          </span>
        </button>
        <NextButton
          faded
          slate
          sm
          icon="i-lucide-x"
          :is-loading="dismissingId === mention.id"
          :label="t('SICARG_DASHBOARD.MENTIONS.DISMISS')"
          @click="dismissMention(mention)"
        />
      </div>
      <p
        v-if="!mentions.length"
        class="px-4 py-6 text-sm text-center text-n-slate-10"
      >
        {{ t('SICARG_DASHBOARD.MENTIONS.EMPTY') }}
      </p>
    </div>
  </CardLayout>
</template>
